import { Menu, useControllableState } from "@chakra-ui/react"
import { useMemo } from "react"
import IconButton from "../button/IconButton"
import { DarkMode } from "../chakra/ui/color-mode"
import FilterMenuItem from "./FilterMenuItem"
import { Filters, FiltersFilterOptions } from "./types"

// Each filter is joined like an AND clause while
// multiple values for a single filter are joined like an OR clause.

type OnFilterChange<T extends Filters> = (
  filterOptions: FiltersFilterOptions<T> | null
) => void

function FilterMenu<T extends Filters>({
  filters,
  value,
  onChange
}: {
  filters: T
  value: FiltersFilterOptions<T> | null
  onChange: OnFilterChange<T>
}) {
  const [appliedFilters, setAppliedFilters] =
    useControllableState<FiltersFilterOptions<T> | null>({
      value: value || null,
      onChange
    })

  const hasAppliedFilters = useMemo(() => {
    return (
      !!appliedFilters &&
      Object.values(appliedFilters).some((filter) => filter.length > 0)
    )
  }, [appliedFilters])

  return (
    <Menu.Root positioning={{ placement: "bottom-end" }}>
      <Menu.Context>
        {(menu: { open: boolean }) => (
          <>
            <Menu.Trigger asChild>
              <IconButton
                aria-label="Filter"
                icon="settings-horizontal"
                colorPalette="whiteAlpha"
                variant={menu.open || hasAppliedFilters ? "subtle" : "ghost"}
                roundSize={8}
              />
            </Menu.Trigger>
            <DarkMode>
              <Menu.Positioner>
                <Menu.Content zIndex="popover">
                  {Object.keys(filters).map((key) => (
                    <FilterMenuItem
                      key={key}
                      filter={filters[key]}
                      value={appliedFilters?.[key] ?? null}
                      onChange={(filterOptions) => {
                        setAppliedFilters((prev) => ({
                          ...(prev || ({} as FiltersFilterOptions<T>)),
                          [key]: filterOptions
                        }))
                      }}
                    />
                  ))}
                </Menu.Content>
              </Menu.Positioner>
            </DarkMode>
          </>
        )}
      </Menu.Context>
    </Menu.Root>
  )
}

export default FilterMenu
